import { transformSDateToString, transformStringToSDate } from "@/shared/utils";
import type { Person } from "@/services/api";
import type { GeneralInfoTabFormValues } from "../schema";
import type {
  CreatePersonRequest,
  UpdatePersonRequest,
} from "@/services/api/entities/person/types";

export const getFullStreetNameFromContactAddress = (
  address: GeneralInfoTabFormValues["addresses"][number],
): string => {
  const addressString = [
    address.addressStreet1,
    address.addressStreet2,
    address.addressCity,
    address.addressCountry,
    address.addressPostalCode,
  ]
    .filter(Boolean)
    .join(", ");
  return addressString;
};

export const transformGeneralInfoFormDataToUpdatePerson = ({
  data,
  defaultValuesPerson,
}: {
  data: GeneralInfoTabFormValues;
  defaultValuesPerson: Person;
}): UpdatePersonRequest => {
  return {
    ...defaultValuesPerson,
    additionalInformation: data.additionalInformation ?? null,
    addresses: data.addresses.map((item) => ({
      ...(item.id && { id: item.id }),
      personId: item.personId,
      addressType: { name: item.addressType?.name ?? "" },
      addressCity: item.addressCity ?? "",
      addressCountry: item.addressCountry ?? "",
      addressPostalCode: item.addressPostalCode ?? "",
      addressStreet1: item.addressStreet1 ?? "",
      addressStreet2: item.addressStreet2 ?? "",
    })),
    anniversaries: data.anniversaries.map((item) => ({
      anniversary: "",
      description: item.description ?? "",
      anniversaryType: item.anniversaryType ?? "",
      createdDate: "",
      ...(item.id && { id: item.id }),
      anniversarySDate: transformStringToSDate(item.anniversary ?? ""),
      personId: item.personId,
    })),
    avatarFile: data.avatarFile?.fileId
      ? {
          fileId: data.avatarFile.fileId,
          fileName: data.avatarFile.fileName ?? "",
          type: data.avatarFile.type ?? "",
        }
      : null,
    birthday: "",
    birthdaySDate: transformStringToSDate(data.birthday ?? ""),
    title: data.title ?? "",
    firstName: data.firstName ?? "",
    gender: data.gender ?? "NOT_DEFINED",
    lastName: data.lastName ?? "",
    middleName: data.middleName ?? "",
    group: data.group ?? null,
    emails: data.emails.map((item) => ({
      email: item.email ?? "",
      personId: item.personId,
      ...(item.id && { id: item.id }),
      emailType: { name: item.emailType?.name ?? "" },
    })),
    phones: data.phones.map((item) => ({
      ...(item.id && { id: item.id }),
      number: item.number ?? "",
      personId: item.personId,
      phoneType: { name: item.phoneType?.name ?? "" },
    })),
    socialNetworks: data.socialNetworks.map((item) => ({
      personId: item.personId,
      ...(item.id && { id: item.id }),
      socialNetworkType: { name: item.socialNetworkType?.name ?? "" },
      socialNetwork: item.socialNetwork ?? "",
    })),
    tags: data.tags.filter((item) => item.name) as Person["tags"],
    websites:
      data?.websites.map((item) => ({
        ...(item.id && { id: item.id }),
        personId: item.personId,
        websiteType: { name: item.websiteType?.name ?? "" },
        websiteAddress: item.websiteAddress ?? "",
      })) ?? [],
  };
};

export const transformGeneralInfoFormDataToCreatePerson = ({
  data,
  defaultValuesPerson,
}: {
  data: GeneralInfoTabFormValues;
  defaultValuesPerson: Person;
}): CreatePersonRequest => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, ...rest } = transformGeneralInfoFormDataToUpdatePerson({
    data,
    defaultValuesPerson,
  });

  return rest;
};

export const getDefaultGeneralTabInfoFormValues = (
  currentPerson: Person,
): GeneralInfoTabFormValues => {
  return {
    id: currentPerson.id,
    websites: currentPerson.websites,
    avatarFile: currentPerson.avatarFile,
    addresses:
      currentPerson?.addresses.map((item) => ({
        ...item,
        personId: item.personId ?? "",
      })) ?? [],
    anniversaries:
      currentPerson?.anniversaries.map((item) => ({
        ...item,
        anniversary: transformSDateToString(item.anniversarySDate),
      })) ?? [],
    birthday: transformSDateToString(currentPerson.birthdaySDate),
    emails: currentPerson?.emails ?? [],
    firstName: currentPerson?.firstName ?? "",
    gender: currentPerson?.gender ?? "NOT_DEFINED",
    group: currentPerson.group ?? undefined,
    lastName: currentPerson?.lastName ?? "",
    middleName: currentPerson?.middleName ?? "",
    phones: currentPerson?.phones ?? [],
    socialNetworks: currentPerson?.socialNetworks ?? [],
    tags: currentPerson?.tags ?? [],
    title: currentPerson?.title ?? "",
    additionalInformation: currentPerson?.additionalInformation ?? "",
  };
};
